import * as React from 'react';
import { createContext, PropsWithChildren, useContext, useReducer } from 'react';

import { IBranding, IDestination, ILob, ISearch } from 'features/search-widget';

interface ISearchWidgetState {
  branding: IBranding;
  lobs: ILob[];
  search: ISearch;
  selectedLob: ILob;
  selectedLobIndex: number;
  fromDestination: IDestination;
  toDestination: IDestination;
  startDate: Date;
  endDate: Date;
}

const initialState: ISearchWidgetState = {
  branding: undefined,
  lobs: [],
  search: undefined,
  selectedLob: undefined,
  selectedLobIndex: 0,
  fromDestination: undefined,
  toDestination: undefined,
  startDate: undefined,
  endDate: undefined,
};

const actions = {
  SET_BRANDING: 'SET_BRANDING',
  SET_LOBS: 'SET_LOBS',
  SET_SEARCH: 'SET_SEARCH',
  SET_SELECTED_LOB_INDEX: 'SET_SELECTED_LOB_INDEX',
  SET_FROM_DESTINATION: 'SET_FROM_DESTINATION',
  SET_TO_DESTINATION: 'SET_TO_DESTINATION',
  SET_START_DATE: 'SET_START_DATE',
  SET_END_DATE: 'SET_END_DATE',
};

function reducer(state: ISearchWidgetState, action): ISearchWidgetState {
  switch (action.type) {
    case actions.SET_BRANDING: {
      return { ...state, branding: action.value };
    }
    case actions.SET_LOBS: {
      const lobs = action.value as ILob[];
      return { ...state, lobs, selectedLob: lobs[state.selectedLobIndex] };
    }
    case actions.SET_SEARCH: {
      return { ...state, search: action.value };
    }
    case actions.SET_SELECTED_LOB_INDEX: {
      const selectedLobIndex = action.value as number;
      return { ...state, selectedLobIndex, selectedLob: state.lobs[selectedLobIndex] };
    }
    case actions.SET_FROM_DESTINATION: {
      return { ...state, fromDestination: action.value };
    }
    case actions.SET_TO_DESTINATION: {
      return { ...state, toDestination: action.value };
    }
    case actions.SET_START_DATE: {
      return { ...state, startDate: action.value };
    }
    case actions.SET_END_DATE: {
      return { ...state, endDate: action.value };
    }
    default: {
      return state;
    }
  }
}

interface ISearchWidgetContext extends ISearchWidgetState {
  setBranding(value): void;
  setLobs(value): void;
  setSearch(value): void;
  setSelectedLobIndex(value): void;
  setFromDestination(value): void;
  setToDestination(value): void;
  setStartDate(value): void;
  setEndDate(value): void;
}

const SearchWidgetContext = createContext<ISearchWidgetContext>({
  ...initialState,
  setBranding: undefined,
  setLobs: undefined,
  setSearch: undefined,
  setSelectedLobIndex: undefined,
  setFromDestination: undefined,
  setToDestination: undefined,
  setStartDate: undefined,
  setEndDate: undefined,
});

function SearchWidgetProvider({ children }: PropsWithChildren<unknown>): JSX.Element {
  const [state, dispatch] = useReducer(reducer, initialState);

  const value: ISearchWidgetContext = {
    ...state,
    setBranding(value) {
      dispatch({ type: actions.SET_BRANDING, value });
    },
    setLobs(value) {
      dispatch({ type: actions.SET_LOBS, value });
    },
    setSearch(value) {
      dispatch({ type: actions.SET_SEARCH, value });
    },
    setSelectedLobIndex(value) {
      dispatch({ type: actions.SET_SELECTED_LOB_INDEX, value });
    },
    setFromDestination(value) {
      dispatch({ type: actions.SET_FROM_DESTINATION, value });
    },
    setToDestination(value) {
      dispatch({ type: actions.SET_TO_DESTINATION, value });
    },
    setStartDate(value) {
      dispatch({ type: actions.SET_START_DATE, value });
    },
    setEndDate(value) {
      dispatch({ type: actions.SET_END_DATE, value });
    },
  };

  return <SearchWidgetContext.Provider value={value}>{children}</SearchWidgetContext.Provider>;
}

function useSearchWidgetContext(): ISearchWidgetContext {
  return useContext(SearchWidgetContext);
}

export { SearchWidgetProvider, useSearchWidgetContext };
